import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { api } from '../../../api/client';
import type { Member, CarouselEvent, EventsResponse } from '../../../types';
import { useFaceDetection, preloadFaceModels } from '../../../hooks/useFaceDetection';
import { extractPhotoMetadata } from '../../../utils/extractPhotoMetadata';
import { clusterPhotos, type StagedPhotoMeta } from '../../../utils/bulkImportClustering';
import { curateCluster, rankFeaturedPhotos } from '../../../utils/burstGrouping';
import { generateTitle, generateDescription } from '../../../utils/generateClusterSummary';
import { runWithConcurrency } from '../../../utils/concurrencyPool';
import { findSuggestedEventMatch } from '../../../utils/existingEventMatch';
import { suggestClusterTemplateKind } from '../../../utils/suggestClusterTemplateKind';
import { generatePhotoCaption } from '../../../utils/generatePhotoCaption';
import { DEFAULT_TEMPLATE_KIND, type TemplateKind } from '../../../utils/eventTemplates';
import { ClusterReviewCard } from './ClusterReviewCard';
import { UnsortedTray } from './UnsortedTray';
import { ImportSummary } from './ImportSummary';
import { computeInvitablePeople } from './computeInvitablePeople';
import type { ClusterState, BulkStagedFace, UploadTask, ImportResult } from './types';
import { PHOTO_ACCEPT } from '../../../utils/photoUpload';

const MAX_PHOTOS = 300;
const METADATA_CONCURRENCY = 8;
const UPLOAD_CONCURRENCY = 4;
const GEOCODE_DELAY_MS = 1100;

function makeClientId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function reverseGeocode(gps: { lat: number; lng: number }): Promise<string | null> {
  try {
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${gps.lat}&lon=${gps.lng}&zoom=10`,
      { headers: { Accept: 'application/json', 'User-Agent': 'EventsCarousel/1.0 (family memory app)' } }
    );
    if (!resp.ok) return null;
    const data = await resp.json();
    const addr = data.address ?? {};
    const city = addr.city ?? addr.town ?? addr.village ?? addr.county ?? '';
    const country = addr.country ?? '';
    const place = [city, country].filter(Boolean).join(', ');
    return place || null;
  } catch {
    return null;
  }
}

function toStagedFace(f: { cropUrl: string; eyesOpenScore: number; faceAreaRatio: number }): BulkStagedFace {
  return {
    cropUrl: f.cropUrl,
    assignedUserId: null,
    eyesOpenScore: f.eyesOpenScore,
    faceAreaRatio: f.faceAreaRatio,
  };
}

function initClusterState(id: string, photos: StagedPhotoMeta[]): ClusterState {
  const { faceCandidates, featured } = curateCluster(photos, []);
  const start = photos[0].takenAt!;
  const end = photos[photos.length - 1].takenAt!;
  const summaryInput = { photoCount: photos.length, startDate: start, endDate: end, location: null, participantNames: [] };
  return {
    id,
    photos,
    title: generateTitle(summaryInput),
    description: generateDescription(summaryInput),
    eventDate: format(start, 'yyyy-MM-dd'),
    location: '',
    titleEdited: false,
    descriptionEdited: false,
    locationEdited: false,
    selectedIds: new Set(featured.map(p => p.clientId)),
    selectionEdited: false,
    expanded: false,
    faceCandidates,
    facesByPhoto: {},
    detecting: false,
    geocoding: false,
    attachToEventId: null,
    templateKind: DEFAULT_TEMPLATE_KIND,
    templateKindEdited: false,
    suggestedTemplateKind: null,
    suggestedTemplateDismissed: false,
    captionsByPhoto: {},
    captionsEditedByPhoto: {},
  };
}

function memberNames(members: Member[], userIds: string[]): string[] {
  const byId = new Map(members.map(m => [m.user.id, m.user.name]));
  return userIds.map(id => byId.get(id)).filter((n): n is string => Boolean(n));
}

export function BulkImportPage() {
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [existingEvents, setExistingEvents] = useState<CarouselEvent[]>([]);
  const [phase, setPhase] = useState<'select' | 'processing' | 'review' | 'importing' | 'done'>('select');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [clusters, setClusters] = useState<ClusterState[]>([]);
  const [unsorted, setUnsorted] = useState<StagedPhotoMeta[]>([]);
  const [pickerError, setPickerError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { detectFaces } = useFaceDetection();

  useEffect(() => {
    preloadFaceModels();
    api.get('/account/members')
      .then(({ data }) => setMembers(Array.isArray(data) ? data : []))
      .catch(() => {});
    api.get<EventsResponse>('/events?sort=date&allPhotos=true')
      .then(({ data }) => setExistingEvents(Array.isArray(data.events) ? data.events : []))
      .catch(() => {});
  }, []);

  function updateCluster(id: string, patch: Partial<ClusterState> | ((c: ClusterState) => Partial<ClusterState>)) {
    setClusters(prev => prev.map(c => {
      if (c.id !== id) return c;
      const delta = typeof patch === 'function' ? patch(c) : patch;
      return { ...c, ...delta };
    }));
  }

  async function handleFileSelect(files: File[]) {
    if (files.length === 0) return;
    if (files.length > MAX_PHOTOS) {
      setPickerError(`Import up to ${MAX_PHOTOS} photos at a time — you selected ${files.length}. Try a smaller date range or run this again.`);
      return;
    }
    setPickerError('');
    setPhase('processing');
    setProgress({ done: 0, total: files.length });

    let done = 0;
    const results = await runWithConcurrency(files, METADATA_CONCURRENCY, async file => {
      const meta = await extractPhotoMetadata(file);
      done++;
      setProgress({ done, total: files.length });
      return { clientId: makeClientId(), file, ...meta } as StagedPhotoMeta;
    });
    const allMeta = results
      .filter((r): r is PromiseFulfilledResult<StagedPhotoMeta> => r.status === 'fulfilled')
      .map(r => r.value);

    const { clusters: rawClusters, unsorted: unsortedPhotos } = clusterPhotos(allMeta);
    setUnsorted(unsortedPhotos);
    const initial = rawClusters.map(c => initClusterState(c.id, c.photos));
    setClusters(initial);
    setPhase(initial.length > 0 || unsortedPhotos.length > 0 ? 'review' : 'select');

    for (const cluster of initial) {
      await processCluster(cluster.id, cluster.photos, cluster.faceCandidates);
    }
  }

  async function processCluster(id: string, photos: StagedPhotoMeta[], faceCandidates: StagedPhotoMeta[]) {
    const gpsPhoto = photos.find(p => p.gps);
    if (gpsPhoto?.gps) {
      updateCluster(id, { geocoding: true });
      const place = await reverseGeocode(gpsPhoto.gps);
      await sleep(GEOCODE_DELAY_MS);
      updateCluster(id, c => ({
        geocoding: false,
        location: c.locationEdited ? c.location : (place ?? c.location),
      }));
    }

    updateCluster(id, { detecting: true });
    const facesByPhoto: Record<string, BulkStagedFace[]> = {};
    for (const photo of faceCandidates) {
      const blobUrl = URL.createObjectURL(photo.file);
      try {
        const faces = await detectFaces(blobUrl);
        facesByPhoto[photo.clientId] = faces.map(toStagedFace);
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
    }
    updateCluster(id, { detecting: false, facesByPhoto });
    refineFeatured(id, faceCandidates, facesByPhoto);
  }

  function refineFeatured(id: string, faceCandidates: StagedPhotoMeta[], facesByPhoto: Record<string, BulkStagedFace[]>) {
    const faceSignals = Object.entries(facesByPhoto).map(([clientId, faces]) => ({
      clientId,
      faceCount: faces.length,
      maxFaceAreaRatio: faces.length ? Math.max(...faces.map(f => f.faceAreaRatio)) : 0,
      eyesOpenScore: faces.length ? faces.reduce((s, f) => s + f.eyesOpenScore, 0) / faces.length : 0,
    }));
    const { featured } = rankFeaturedPhotos(faceCandidates, faceSignals);
    // No auto-matched names to seed the initial title/description with —
    // face recognition/matching was removed entirely; participants are only
    // known once a person manually tags a detected face crop, which happens
    // after this initial pass.
    const clusterNames: string[] = [];

    updateCluster(id, c => {
      const patch: Partial<ClusterState> = {};
      if (!c.selectionEdited) patch.selectedIds = new Set(featured.map(p => p.clientId));
      if (!c.titleEdited || !c.descriptionEdited) {
        const start = c.photos[0].takenAt!;
        const end = c.photos[c.photos.length - 1].takenAt!;
        const input = { photoCount: c.photos.length, startDate: start, endDate: end, location: c.location || null, participantNames: clusterNames };
        if (!c.titleEdited) patch.title = generateTitle(input);
        if (!c.descriptionEdited) patch.description = generateDescription(input);
      }

      if (!c.templateKindEdited) {
        patch.suggestedTemplateKind = suggestClusterTemplateKind({
          photos: c.photos.map(p => ({ takenAt: p.takenAt, gps: p.gps })),
          participantNames: clusterNames,
        });
      }

      const nextCaptions: Record<string, string> = { ...c.captionsByPhoto };
      for (const photo of c.photos) {
        if (c.captionsEditedByPhoto[photo.clientId]) continue;
        const photoNames = memberNames(
          members,
          (facesByPhoto[photo.clientId] ?? []).filter(f => f.assignedUserId).map(f => f.assignedUserId as string)
        );
        nextCaptions[photo.clientId] = generatePhotoCaption({
          templateKind: c.templateKind,
          takenAt: photo.takenAt,
          location: c.location || null,
          participantNames: photoNames,
        });
      }
      patch.captionsByPhoto = nextCaptions;

      return patch;
    });
  }

  function setClusterTemplateKind(id: string, kind: TemplateKind) {
    updateCluster(id, c => {
      const nextCaptions: Record<string, string> = { ...c.captionsByPhoto };
      for (const photo of c.photos) {
        if (c.captionsEditedByPhoto[photo.clientId]) continue;
        const photoNames = memberNames(
          members,
          (c.facesByPhoto[photo.clientId] ?? []).filter(f => f.assignedUserId).map(f => f.assignedUserId as string)
        );
        nextCaptions[photo.clientId] = generatePhotoCaption({
          templateKind: kind,
          takenAt: photo.takenAt,
          location: c.location || null,
          participantNames: photoNames,
        });
      }
      return { templateKind: kind, templateKindEdited: true, captionsByPhoto: nextCaptions };
    });
  }

  function dismissTemplateSuggestion(id: string) {
    updateCluster(id, { suggestedTemplateDismissed: true });
  }

  function setPhotoCaption(clusterId: string, photoClientId: string, value: string) {
    updateCluster(clusterId, c => ({
      captionsByPhoto: { ...c.captionsByPhoto, [photoClientId]: value },
      captionsEditedByPhoto: { ...c.captionsEditedByPhoto, [photoClientId]: true },
    }));
  }

  function removeCluster(id: string) {
    setClusters(prev => prev.filter(c => c.id !== id));
  }

  function mergeWithNext(id: string) {
    setClusters(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx === -1 || idx === prev.length - 1) return prev;
      const a = prev[idx];
      const b = prev[idx + 1];
      const mergedPhotos = [...a.photos, ...b.photos].sort((x, y) => x.takenAt!.getTime() - y.takenAt!.getTime());
      const merged = initClusterState(a.id, mergedPhotos);
      const next = prev.slice();
      next.splice(idx, 2, merged);
      processCluster(merged.id, merged.photos, merged.faceCandidates);
      return next;
    });
  }

  function moveToNewEvent(clusterId: string, clientId: string) {
    setClusters(prev => {
      const idx = prev.findIndex(c => c.id === clusterId);
      if (idx === -1) return prev;
      const cluster = prev[idx];
      const moving = cluster.photos.find(p => p.clientId === clientId);
      if (!moving || cluster.photos.length <= 1) return prev;
      const remaining = cluster.photos.filter(p => p.clientId !== clientId);
      const updatedOriginal = initClusterState(cluster.id, remaining);
      const created = initClusterState(makeClientId(), [moving]);
      const next = prev.slice();
      next.splice(idx, 1, updatedOriginal, created);
      processCluster(updatedOriginal.id, updatedOriginal.photos, updatedOriginal.faceCandidates);
      processCluster(created.id, created.photos, created.faceCandidates);
      return next;
    });
  }

  function excludePhoto(clusterId: string, clientId: string) {
    setClusters(prev => {
      const cluster = prev.find(c => c.id === clusterId);
      if (!cluster) return prev;
      const remaining = cluster.photos.filter(p => p.clientId !== clientId);
      if (remaining.length === 0) return prev.filter(c => c.id !== clusterId);
      const updated = initClusterState(cluster.id, remaining);
      processCluster(updated.id, updated.photos, updated.faceCandidates);
      return prev.map(c => (c.id === clusterId ? updated : c));
    });
  }

  function assignToCluster(clientId: string, targetClusterId: string) {
    const photo = unsorted.find(p => p.clientId === clientId);
    if (!photo) return;
    setUnsorted(prev => prev.filter(p => p.clientId !== clientId));
    setClusters(prev => prev.map(c => {
      if (c.id !== targetClusterId) return c;
      return { ...c, photos: [...c.photos, photo], selectedIds: new Set([...c.selectedIds, photo.clientId]) };
    }));
  }

  function assignToExistingEvent(clientId: string, eventId: string) {
    const photo = unsorted.find(p => p.clientId === clientId);
    if (!photo) return;
    setUnsorted(prev => prev.filter(p => p.clientId !== clientId));
    setClusters(prev => {
      const existingRow = prev.find(c => c.attachToEventId === eventId);
      if (existingRow) {
        return prev.map(c => (c.id === existingRow.id
          ? { ...c, photos: [...c.photos, photo], selectedIds: new Set([...c.selectedIds, photo.clientId]) }
          : c));
      }
      const event = existingEvents.find(e => e.id === eventId);
      const { faceCandidates, featured } = curateCluster([photo], []);
      const row: ClusterState = {
        id: makeClientId(),
        photos: [photo],
        title: event?.title ?? 'Event',
        description: '',
        eventDate: '',
        location: '',
        titleEdited: true,
        descriptionEdited: true,
        locationEdited: false,
        selectedIds: new Set(featured.map(p => p.clientId)),
        selectionEdited: false,
        expanded: false,
        faceCandidates,
        facesByPhoto: {},
        detecting: false,
        geocoding: false,
        attachToEventId: eventId,
        templateKind: DEFAULT_TEMPLATE_KIND,
        templateKindEdited: false,
        suggestedTemplateKind: null,
        suggestedTemplateDismissed: false,
        captionsByPhoto: {},
        captionsEditedByPhoto: {},
      };
      processCluster(row.id, row.photos, row.faceCandidates);
      return [...prev, row];
    });
  }

  function discardUnsorted(clientId: string) {
    setUnsorted(prev => prev.filter(p => p.clientId !== clientId));
  }

  function attachToExistingEvent(clusterId: string, eventId: string) {
    updateCluster(clusterId, { attachToEventId: eventId });
  }

  function undoAttach(clusterId: string) {
    updateCluster(clusterId, { attachToEventId: null });
  }

  async function handleImportAll() {
    setSubmitError('');
    setPhase('importing');

    const newEventClusters = clusters.filter(c => !c.attachToEventId);

    const bulkEvents = newEventClusters.map(c => ({
      clientId: c.id,
      title: c.title || 'Untitled Event',
      description: c.description || undefined,
      eventDate: c.eventDate || undefined,
      location: c.location || undefined,
      templateKind: c.templateKind,
    }));

    let idByClientId: Map<string, string> = new Map();
    if (bulkEvents.length > 0) {
      try {
        const { data } = await api.post('/events/bulk', { events: bulkEvents });
        idByClientId = new Map(data.events.map((e: { clientId: string; id: string }) => [e.clientId, e.id]));
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to create events';
        setSubmitError(msg);
        setPhase('review');
        return;
      }
    }

    const tasks: UploadTask[] = clusters.flatMap(c => {
      const eventId = c.attachToEventId ?? idByClientId.get(c.id);
      if (!eventId) return [];
      return c.photos
        .filter(p => c.selectedIds.has(p.clientId))
        .map(p => ({ clusterId: c.id, clusterTitle: c.title, photo: p, eventId, status: 'pending' as const }));
    });

    await runUploadTasks(tasks);

    setImportResult({
      tasks,
      eventChips: clusters.map(c => {
        if (c.attachToEventId) {
          const existing = existingEvents.find(e => e.id === c.attachToEventId);
          return { id: c.attachToEventId, title: existing?.title ?? c.title };
        }
        return { id: idByClientId.get(c.id) ?? c.id, title: c.title };
      }),
    });
    setPhase('done');
  }

  async function runUploadTasks(tasks: UploadTask[]) {
    setUploadProgress({ done: 0, total: tasks.length });
    let done = 0;
    await runWithConcurrency(tasks, UPLOAD_CONCURRENCY, async task => {
      task.status = 'uploading';
      try {
        const form = new FormData();
        form.append('photo', task.photo.file);
        if (task.photo.takenAt) form.append('takenAt', task.photo.takenAt.toISOString());
        if (task.photo.gps) {
          form.append('gpsLat', String(task.photo.gps.lat));
          form.append('gpsLng', String(task.photo.gps.lng));
        }
        const cluster = clusters.find(c => c.id === task.clusterId);
        const caption = cluster?.captionsByPhoto[task.photo.clientId];
        if (caption) form.append('caption', caption);
        const { data: uploaded } = await api.post(`/events/${task.eventId}/photos`, form);

        const faces = cluster?.facesByPhoto[task.photo.clientId] ?? [];
        for (const f of faces) {
          if (f.assignedUserId) {
            try {
              await api.post(`/events/${task.eventId}/photos/${uploaded.id}/participants`, { userId: f.assignedUserId });
            } catch {
              // non-fatal — the photo itself already uploaded successfully
            }
          }
        }
        task.status = 'done';
      } catch {
        task.status = 'failed';
      } finally {
        done++;
        setUploadProgress({ done, total: tasks.length });
      }
    });
  }

  async function retryFailed() {
    if (!importResult) return;
    const failed = importResult.tasks.filter(t => t.status === 'failed');
    if (failed.length === 0) return;
    setPhase('importing');
    await runUploadTasks(failed);
    setImportResult({ ...importResult, tasks: [...importResult.tasks] });
    setPhase('done');
  }

  const totalPhotos = clusters.reduce((sum, c) => sum + c.photos.length, 0);
  const selectedPhotos = clusters.reduce((sum, c) => sum + c.selectedIds.size, 0);

  if (phase === 'done' && importResult) {
    const invitablePeople = computeInvitablePeople(clusters, members, importResult.eventChips);

    return (
      <ImportSummary
        result={importResult}
        onRetry={retryFailed}
        onViewEvents={() => navigate('/events')}
        uploadProgress={uploadProgress}
        importing={false}
        invitablePeople={invitablePeople}
        onInvited={memberId => setMembers(prev => prev.filter(m => m.id !== memberId))}
      />
    );
  }

  return (
    <div className="max-w-3xl pb-24">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-2">Bulk Import</h1>
      <p className="text-sm text-slate-500 mb-6">
        Pick a batch of photos and we'll group them into candidate events automatically — review and edit before anything is created.
      </p>

      {phase === 'select' && (
        <div className="bg-white rounded-2xl p-8 border-2 border-dashed border-indigo-200 text-center">
          <p className="text-sm text-slate-500 mb-4">Select photos from your library — up to {MAX_PHOTOS} at a time.</p>
          {pickerError && <p className="text-sm text-red-600 mb-3">{pickerError}</p>}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-200"
          >
            Choose Photos
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={PHOTO_ACCEPT}
            multiple
            className="hidden"
            onChange={e => handleFileSelect(Array.from(e.target.files ?? []))}
          />
        </div>
      )}

      {phase === 'processing' && (
        <div className="bg-white rounded-2xl p-6 border border-indigo-100">
          <p className="text-sm font-medium text-slate-700 mb-2">Reading photo details…</p>
          <div className="h-1.5 bg-indigo-50 rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-indigo-500 transition-all"
              style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-slate-400">{progress.done} / {progress.total}</p>
        </div>
      )}

      {(phase === 'review' || phase === 'importing') && (
        <>
          {submitError && <p className="text-sm text-red-600 mb-4">{submitError}</p>}
          <p className="text-sm font-semibold text-slate-700 mb-3">
            {clusters.length} candidate event{clusters.length === 1 ? '' : 's'} found — review before importing
          </p>
          <div className="space-y-4">
            {clusters.map(cluster => (
              <ClusterReviewCard
                key={cluster.id}
                cluster={cluster}
                members={members}
                disabled={phase === 'importing'}
                suggestedMatch={cluster.attachToEventId ? null : findSuggestedEventMatch(cluster, existingEvents)}
                attachedEventTitle={cluster.attachToEventId ? existingEvents.find(e => e.id === cluster.attachToEventId)?.title ?? null : null}
                onAttachToExisting={eventId => attachToExistingEvent(cluster.id, eventId)}
                onUndoAttach={() => undoAttach(cluster.id)}
                onFieldChange={(field, value) => updateCluster(cluster.id, {
                  [field]: value,
                  ...(field === 'title' ? { titleEdited: true } : {}),
                  ...(field === 'description' ? { descriptionEdited: true } : {}),
                  ...(field === 'location' ? { locationEdited: true } : {}),
                })}
                onToggleExpanded={() => updateCluster(cluster.id, { expanded: !cluster.expanded })}
                onTogglePhoto={clientId => updateCluster(cluster.id, c => {
                  const next = new Set(c.selectedIds);
                  if (next.has(clientId)) next.delete(clientId); else next.add(clientId);
                  return { selectedIds: next, selectionEdited: true };
                })}
                onAssignFace={(photoClientId, faceIdx, userId) => {
                  updateCluster(cluster.id, c => {
                    const faces = c.facesByPhoto[photoClientId] ?? [];
                    if (!faces[faceIdx]) return {};
                    const nextFaces = faces.map((f, i) => (i === faceIdx ? { ...f, assignedUserId: userId } : f));
                    return { facesByPhoto: { ...c.facesByPhoto, [photoClientId]: nextFaces } };
                  });
                }}
                onAddNewPerson={async (photoClientId, faceIdx, name) => {
                  const { data } = await api.post('/account/members', { name });
                  setMembers(prev => [...prev, data]);
                  updateCluster(cluster.id, c => {
                    const faces = c.facesByPhoto[photoClientId] ?? [];
                    const nextFaces = faces.map((f, i) => (i === faceIdx ? { ...f, assignedUserId: data.user.id } : f));
                    return { facesByPhoto: { ...c.facesByPhoto, [photoClientId]: nextFaces } };
                  });
                }}
                onExcludePhoto={clientId => excludePhoto(cluster.id, clientId)}
                onRemoveCluster={() => removeCluster(cluster.id)}
                onMergeWithNext={() => mergeWithNext(cluster.id)}
                onMoveToNewEvent={clientId => moveToNewEvent(cluster.id, clientId)}
                onTemplateKindChange={kind => setClusterTemplateKind(cluster.id, kind)}
                onDismissTemplateSuggestion={() => dismissTemplateSuggestion(cluster.id)}
                onPhotoCaptionChange={(photoClientId, value) => setPhotoCaption(cluster.id, photoClientId, value)}
              />
            ))}
          </div>

          {unsorted.length > 0 && (
            <UnsortedTray
              photos={unsorted}
              clusters={clusters}
              existingEvents={existingEvents}
              onAssign={assignToCluster}
              onAssignToExistingEvent={assignToExistingEvent}
              onDiscard={discardUnsorted}
            />
          )}

          <div className="fixed bottom-0 left-0 right-0 sm:sticky sm:bottom-4 sm:mt-6 bg-slate-900 sm:rounded-xl px-4 sm:px-5 py-3 flex items-center justify-between gap-3 text-white shadow-lg" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
            <span className="text-sm font-medium">
              {clusters.length} event{clusters.length === 1 ? '' : 's'} · {selectedPhotos} of {totalPhotos} photos
              {unsorted.length > 0 && <> · {unsorted.length} unsorted</>}
            </span>
            <button
              type="button"
              onClick={handleImportAll}
              disabled={phase === 'importing' || clusters.length === 0}
              className="px-4 py-2 bg-white text-slate-900 rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors"
            >
              {phase === 'importing' ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…` : 'Import All →'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
